/**
 * The pieces every panel of the order's chain shares (Ben, 3 Oct; the
 * journeys branch's rounds 16b–16c): the old Lines and Delivery tabs folded
 * into the rail's steps, one panel open at a time, at `?step=…&line=…`.
 *
 * - a heading with its ⓘ, a summary and one action on its row;
 * - the rows themselves are the rail's own (`RailRow`): node · label · name
 *   · status · one outlined button · an ⓘ that opens the row's detail in a
 *   dialog;
 * - the focused line (`?line=`) scrolled to, with a quiet left border.
 */
import { useCallback, type ReactNode } from "react";
import { Info } from "lucide-react";
import type {
  StepProblem,
  StepState,
} from "@shared-core/domain/lifecycle-rail";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../primitives/dialog";
import { cn } from "../../primitives/utils";
import { InfoTip } from "../../patterns/InfoTip";

/** A ref for the focused row: it scrolls into view as it mounts. */
export function useFocusRef() {
  return useCallback((row: HTMLElement | null) => {
    row?.scrollIntoView({ block: "center" });
  }, []);
}

/** A row's status tone, as the rail's node: done is done, a problem its dot. */
export function nodeOfTone(
  tone: "neutral" | "at_risk" | "late" | "done",
  started = true,
): { state: StepState; problem: StepProblem | null } {
  switch (tone) {
    case "done":
      return { state: "done", problem: null };
    case "late":
    case "at_risk":
      return { state: "in_progress", problem: tone };
    case "neutral":
      return { state: started ? "in_progress" : "to_do", problem: null };
  }
}

export function PanelHeading({
  title,
  level = 3,
  info,
  infoLabel,
  detail,
  summary,
  action,
}: {
  title: string;
  /** 3 under the rail; 2 where it heads a tab of its own; 4 for a group inside a panel (the Delivery step's delivery charges). */
  level?: 2 | 3 | 4;
  info?: ReactNode;
  infoLabel?: string;
  /** One muted line under the title ("From PrintCo · Jo Smith, 0113 496 0000"). */
  detail?: ReactNode;
  /** A one-line summary on the heading's row. */
  summary?: ReactNode;
  /** One step for the whole panel ("Release all ready lines (2)"). */
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          {level === 2 ? (
            <h2 className="text-base font-semibold">{title}</h2>
          ) : level === 3 ? (
            <h3 className="text-sm font-semibold">{title}</h3>
          ) : (
            <h4 className="text-sm font-medium">{title}</h4>
          )}
          {info && (
            <InfoTip label={infoLabel ?? `About ${title.toLowerCase()}`}>
              {info}
            </InfoTip>
          )}
        </div>
        {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {summary && (
          <p className="text-sm tabular-nums text-muted-foreground">
            {summary}
          </p>
        )}
        {action}
      </div>
    </div>
  );
}

/** A row's ⓘ, at its end: its detail (what a tab's ▸ used to open), in a dialog. */
export function ViewDetail({
  title,
  description,
  children,
}: {
  /** "Line .02 Pull-up banner" */
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="size-7 px-0 text-muted-foreground hover:text-foreground"
          aria-label={`View detail of ${title}`}
        >
          <Info aria-hidden className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3 text-sm">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

/** A detail's label and value, side by side. */
export function DetailItem({
  label,
  wide = false,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("min-w-0", wide && "col-span-2")}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}
